import { useState, useEffect, useRef, type FormEvent } from 'react';
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '@/utils/userFeedback';

/**
 * FeedbackForm
 *
 * Zero-backend in-app feedback modal. On submit it persists the entry to
 * localStorage (audit log) and, when VITE_FEEDBACK_EMAIL is configured, opens
 * the user's mail client with a pre-filled message.
 *
 * Adapted from the feedback-widget mailto pattern:
 *   - No i18n (plain string literals)
 *   - No FocusManager dependency (simple contentRef focus + Escape handling)
 *   - Styled with the app's CSS variable design tokens
 *   - z-[1000] so the modal clears the calc-nav SettingsDrawer (z-index 900/901)
 */

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  // `| undefined` keeps this compatible with `exactOptionalPropertyTypes`
  // when callers pass explicit `undefined` (e.g. from a partial config object).
  initialType?: UserFeedback['type'] | undefined;
  initialCategory?: string | undefined;
  initialError?: string | undefined;
}

const FEEDBACK_TYPES: { value: UserFeedback['type']; label: string }[] = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'usability_issue', label: 'Usability Issue' },
  { value: 'performance_issue', label: 'Performance Issue' },
];

const CATEGORIES = [
  'general',
  'gpa_calculation',
  'course_entry',
  'ui_ux',
  'bug',
  'feature_request',
  'accessibility',
  'data_import_export',
];

const PRIORITIES: { value: UserFeedback['priority']; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const formatLabel = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export function FeedbackForm({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}: FeedbackFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  });

  const contentRef = useRef<HTMLDivElement>(null);

  // Reset form state whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  // Focus the panel on open and close on Escape
  useEffect(() => {
    if (!isOpen) return;
    contentRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);

      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;

      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-surface shadow-xl outline-none"
      >
        <div className="p-6">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <h2 id="feedback-title" className="text-2xl font-semibold text-[var(--color-text)]">
              Send Feedback
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {submitted ? (
            /* Success state */
            <div className="py-8 text-center">
              <p className="mb-2 text-lg font-medium text-[var(--color-text)]">Thank you!</p>
              <p className="mb-4 text-sm text-[var(--color-text-muted)]">
                Your mail client should have opened. Send the pre-filled email to complete.
              </p>
              {feedbackId && (
                <p className="mb-4 text-xs text-[var(--color-text-muted)]">Reference ID: {feedbackId}</p>
              )}
              <button
                onClick={onClose}
                className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-hover"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type */}
              <div>
                <label htmlFor="fb-type" className="mb-1 block text-sm font-medium text-[var(--color-text)]">
                  Type
                </label>
                <select
                  id="fb-type"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-[var(--color-text)]"
                >
                  {FEEDBACK_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>{ft.label}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="fb-category" className="mb-1 block text-sm font-medium text-[var(--color-text)]">
                  Category
                </label>
                <select
                  id="fb-category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-[var(--color-text)]"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{formatLabel(c)}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <p className="mb-1 block text-sm font-medium text-[var(--color-text)]">Priority</p>
                <div className="flex gap-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => handleChange('priority', p.value)}
                      className={`rounded border px-3 py-1 text-sm font-medium transition-[var(--transition-fast)] ${
                        formData.priority === p.value
                          ? 'border-primary bg-primary text-white'
                          : 'border-border text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="fb-title" className="mb-1 block text-sm font-medium text-[var(--color-text)]">
                  Title *
                </label>
                <input
                  id="fb-title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="One-line summary"
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-[var(--color-text)]"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="fb-description" className="mb-1 block text-sm font-medium text-[var(--color-text)]">
                  Description *
                </label>
                <textarea
                  id="fb-description"
                  rows={5}
                  required
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="What happened? What did you expect instead? Include any steps to reproduce the issue."
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-[var(--color-text)]"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t border-border pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-border px-4 py-2 text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                  className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-hover disabled:opacity-50"
                >
                  {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Floating trigger button ----------

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void;
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps) {
  return (
    <button
      onClick={() => onOpen()}
      className="fixed bottom-4 right-4 z-40 rounded-full bg-primary p-3 text-white shadow-lg transition-[var(--transition-fast)] hover:bg-primary-hover"
      aria-label="Send feedback"
      title="Send feedback"
    >
      {/* Chat bubble icon */}
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    </button>
  );
}
